import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast, Toaster } from "sonner";
import { Camera, Upload, Sparkle, FloppyDisk, VideoCamera, X } from "@phosphor-icons/react";
import { STAGES } from "../lib/stages";
import { buildVCard, downloadVCard } from "../lib/vcard";

// Re-encode uploads (HEIC/huge photos) to a JPEG ≤1600px so the scanner can read them
const toJpegDataUrl = (dataUrl) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * scale);
    c.height = Math.round(img.height * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    resolve(c.toDataURL("image/jpeg", 0.85));
  };
  img.onerror = () => resolve(dataUrl);
  img.src = dataUrl;
});


export default function ScanCard() {
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", title: "", website: "", notes: "", stage: "new", source: "scanned", event_id: "" });
  const [saving, setSaving] = useState(false);
  const [liveOn, setLiveOn] = useState(false);
  const inputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => { api.get("/events").then((r) => setEvents(r.data)); }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      setLiveOn(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 100);
    } catch (e) { toast.error("Camera access denied"); }
  };
  const stopCamera = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setLiveOn(false);
  };
  const capture = async () => {
    if (!videoRef.current) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext("2d").drawImage(videoRef.current, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    stopCamera();
    setPreview(dataUrl);
    setScanning(true);
    try {
      const r = await api.post("/scan-card", { image_base64: dataUrl });
      setResult(r.data);
      setForm((prev) => ({
        name: r.data.name || "", email: r.data.email || "", phone: r.data.phone || "",
        company: r.data.company || "", title: r.data.title || "", website: r.data.website || "",
        notes: r.data.notes || (r.data.address ? `Address: ${r.data.address}` : ""),
        stage: "new", source: "scanned", event_id: prev.event_id || "",
      }));
      toast.success("Card scanned");
    } catch (err) { toast.error(err?.response?.data?.detail || "Scan failed"); }
    finally { setScanning(false); }
  };

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = await toJpegDataUrl(ev.target.result);
      setPreview(dataUrl);
      setScanning(true);
      try {
        const r = await api.post("/scan-card", { image_base64: dataUrl });
        setResult(r.data);
        setForm((prev) => ({
          name: r.data.name || "",
          email: r.data.email || "",
          phone: r.data.phone || "",
          company: r.data.company || "",
          title: r.data.title || "",
          website: r.data.website || "",
          notes: r.data.notes || (r.data.address ? `Address: ${r.data.address}` : ""),
          stage: "new",
          source: "scanned",
          event_id: prev.event_id || "",
        }));
        toast.success("Card scanned");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Scan failed");
      } finally { setScanning(false); }
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.event_id) delete payload.event_id;
      const r = await api.post("/contacts", payload);
      // Also trigger a native .vcf download so the phone (iOS / Android) prompts
      // the user to save this new contact directly to their address book.
      try {
        const vcf = buildVCard({
          name: form.name,
          email: form.email,
          phone: form.phone,
          title: form.title,
          company: form.company,
          website: form.website,
          bio: form.notes,
        });
        const safe = (form.name || "contact").replace(/[^\w\-]+/g, "_");
        downloadVCard(vcf, `${safe}.vcf`);
        toast.success("Saved. Tap the .vcf to add to your phone contacts.");
      } catch {
        toast.success("Contact saved");
      }
      nav(`/contacts/${r.data.id}`);
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="scan-page">
      <Toaster theme="dark" />
      <div className="overline mb-2">// AI scanner</div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-2">Scan a business card</h1>
      <p className="text-[#C7DDE5] max-w-2xl mb-10">Upload or capture a card and Gemini vision extracts every field — <strong style={{ color: "var(--text)" }}>or skip the scan</strong> and type in details yourself. Review, edit, save.</p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <section className="lg:col-span-5">
          <div className="border" style={{ borderColor: "var(--border)" }}>
            {liveOn ? (
              <div className="relative">
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-[360px] object-cover" style={{ background: "#000" }} data-testid="live-video" />
                <button onClick={stopCamera} className="absolute top-3 right-3 bg-black/60 p-2" data-testid="close-camera-btn"><X weight="bold" /></button>
              </div>
            ) : preview ? (
              <img src={preview} alt="preview" className="w-full h-[360px] object-contain" style={{ background: "#000" }} />
            ) : (
              <div className="h-[360px] flex flex-col items-center justify-center grid-bg p-6 text-center">
                <Camera size={56} weight="duotone" color="#2DD4D4" />
                <p className="mt-4 text-[#C7DDE5]">Live scan or upload</p>
                <p className="mt-1 text-xs" style={{ color: "var(--text-3)" }}>Or use the form on the right → to enter manually</p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-0">
            <button onClick={() => inputRef.current?.click()} data-testid="upload-btn"
              className="btn-secondary flex items-center justify-center gap-2 border-t-0">
              <Upload weight="bold" /> Upload
            </button>
            {liveOn ? (
              <button onClick={capture} data-testid="capture-now-btn" className="btn-primary flex items-center justify-center gap-2 border-t-0 border-l-0" style={{ boxShadow: "none" }}>
                <Camera weight="bold" /> Capture
              </button>
            ) : (
              <button onClick={startCamera} data-testid="live-camera-btn"
                className="btn-secondary flex items-center justify-center gap-2 border-t-0 border-l-0">
                <VideoCamera weight="bold" /> Live
              </button>
            )}
            <label className="btn-secondary flex items-center justify-center gap-2 border-t-0 border-l-0 cursor-pointer" data-testid="capture-btn">
              <Camera weight="bold" /> Photo
              <input type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])} />
            </label>
          </div>

          {/* Manual entry callout */}
          <button
            onClick={() => {
              setPreview(null); setResult(null);
              // clear form except event_id, then scroll to review section
              setForm((prev) => ({ name: "", email: "", phone: "", company: "", title: "", website: "", notes: "", stage: "new", source: "manual", event_id: prev.event_id || "" }));
              const el = document.querySelector('[data-testid="scan-name"]');
              if (el) { el.focus(); el.scrollIntoView({ behavior: "smooth", block: "center" }); }
            }}
            data-testid="manual-entry-btn"
            className="mt-4 w-full border p-4 hover:bg-[#15303A] transition flex items-center justify-between group"
            style={{ borderColor: "var(--border)" }}>
            <div className="text-left">
              <div className="overline" style={{ color: "var(--accent)" }}>// no card?</div>
              <div className="font-semibold mt-1">Enter contact manually</div>
              <div className="text-xs mt-1" style={{ color: "var(--text-2)" }}>Type their info in the form on the right instead</div>
            </div>
            <span className="text-2xl" style={{ color: "var(--accent)" }}>→</span>
          </button>
          <input ref={inputRef} type="file" accept="image/*" className="hidden"
            data-testid="file-input" onChange={(e) => handleFile(e.target.files?.[0])} />

          {scanning && (
            <div className="mt-6 border p-4 flex items-center gap-3" style={{ borderColor: "var(--accent)" }}>
              <Sparkle className="pulse-red" weight="fill" color="#2DD4D4" />
              <span className="font-mono text-sm">Extracting contact info...</span>
            </div>
          )}
        </section>

        <section className="lg:col-span-7">
          {/* Event picker — set BEFORE the event to auto-tag every scan */}
          <div className="border p-4 mb-6" style={{ borderColor: form.event_id ? "var(--accent)" : "var(--border)" }} data-testid="scan-event-picker">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="overline" style={form.event_id ? { color: "var(--accent)" } : {}}>// active event {form.event_id ? "· tagged" : "· none"}</div>
              <Link to="/events" className="overline hover:text-white">manage events →</Link>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <select value={form.event_id} onChange={(e) => setForm({ ...form, event_id: e.target.value })} className="input-base flex-1 min-w-[200px]"
                style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }} data-testid="scan-event">
                <option value="">— no event —</option>
                {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}{ev.date ? ` · ${new Date(ev.date).toLocaleDateString()}` : ""}</option>)}
              </select>
            </div>
            <p className="text-xs text-[#C7DDE5] mt-2">Pick an event once — every contact you scan in this session will be tagged.</p>
          </div>

          <div className="border p-6" style={{ borderColor: "var(--border)" }}>
            <div className="overline mb-4">// review &amp; save</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Name *" v={form.name} on={(v) => setForm({ ...form, name: v })} t="scan-name" />
              <F label="Company" v={form.company} on={(v) => setForm({ ...form, company: v })} t="scan-company" />
              <F label="Title" v={form.title} on={(v) => setForm({ ...form, title: v })} t="scan-title" />
              <F label="Email" v={form.email} on={(v) => setForm({ ...form, email: v })} t="scan-email" />
              <F label="Phone" v={form.phone} on={(v) => setForm({ ...form, phone: v })} t="scan-phone" />
              <F label="Website" v={form.website} on={(v) => setForm({ ...form, website: v })} t="scan-website" />
              <div className="md:col-span-2">
                <label className="label-base">Stage</label>
                <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} className="input-base"
                  style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }} data-testid="scan-stage">
                  {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label-base">Notes</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="input-base"
                  style={{ border: "1px solid var(--border)", padding: "10px 12px" }} data-testid="scan-notes" />
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={save} disabled={saving || !form.name} data-testid="save-scanned-btn" className="btn-primary flex items-center gap-2">
                <FloppyDisk weight="bold" /> {saving ? "Saving..." : "Save contact"}
              </button>
              <button onClick={() => { setPreview(null); setResult(null); setForm((prev) => ({ name: "", email: "", phone: "", company: "", title: "", website: "", notes: "", stage: "new", source: "manual", event_id: prev.event_id || "" })); }}
                className="btn-secondary">Clear</button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function F({ label, v, on, t }) {
  return (
    <div>
      <label className="label-base">{label}</label>
      <input data-testid={t} value={v} onChange={(e) => on(e.target.value)} className="input-base" />
    </div>
  );
}
